import { useEffect } from "react";

const FALLBACK_SITE_ORIGIN = "https://flexlearnai.com";

function resolveCanonicalUrl(canonicalPath) {
  const configuredOrigin =
    typeof process.env.REACT_APP_SITE_ORIGIN === "string" &&
    process.env.REACT_APP_SITE_ORIGIN.trim()
      ? process.env.REACT_APP_SITE_ORIGIN.trim().replace(/\/$/, "")
      : FALLBACK_SITE_ORIGIN;

  if (!canonicalPath) {
    return window.location.href.split("#")[0];
  }

  // Cloudflare Pages 308-redirects "/path" to "/path/", so canonicals must use
  // the trailing-slash form — the URL that actually serves a 200.
  const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
  return `${configuredOrigin}${path.endsWith("/") ? path : `${path}/`}`;
}

function resolveImageUrl(image) {
  const configuredOrigin =
    typeof process.env.REACT_APP_SITE_ORIGIN === "string" &&
    process.env.REACT_APP_SITE_ORIGIN.trim()
      ? process.env.REACT_APP_SITE_ORIGIN.trim().replace(/\/$/, "")
      : FALLBACK_SITE_ORIGIN;

  const path = image || "/og-card.png";
  if (/^https?:\/\//.test(path)) {
    return path;
  }
  return `${configuredOrigin}${path.startsWith("/") ? path : `/${path}`}`;
}

function upsertMeta(selector, key, value) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(key, value);
    document.head.appendChild(element);
  }
  return element;
}

function setMeta(selector, key, value, content) {
  upsertMeta(selector, key, value).setAttribute("content", content);
}

export default function PageMeta({
  title,
  description,
  canonicalPath,
  image = "",
  ogType = "website",
}) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }

    if (title) {
      setMeta('meta[property="og:title"]', "property", "og:title", title);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }

    setMeta('meta[property="og:type"]', "property", "og:type", ogType);
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "FlexLearnAI");

    const canonicalUrl = resolveCanonicalUrl(canonicalPath);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);

    const imageUrl = resolveImageUrl(image);
    setMeta('meta[property="og:image"]', "property", "og:image", imageUrl);
    setMeta(
      'meta[property="og:image:alt"]',
      "property",
      "og:image:alt",
      "FlexLearnAI — your AI study coach",
    );
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", imageUrl);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");

    let canonicalTag = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", canonicalUrl);
  }, [canonicalPath, description, title, image, ogType]);

  return null;
}
